// What each day tile of the availability panel says (Booking V3, option-led).
//
// An option that runs at fixed start times (availability_rules kind
// start_times: every band of the day is ONE start) is chosen in the tile
// itself: the tile lists that day's start times as buttons and the length
// once, so one tap picks the day and the start (owner, 24 Sept: "Move the
// starting times and duration in the day/date blocks"). The time strip and
// the "Start times · …" row under the tiles are then not shown.
//
// Every other day keeps its status word: a day whose bands are ranges (the
// guest picks any start in a window) reads "Free" and keeps the time strip
// and the start chips below, exactly as before; a day with no start reads
// Closed / Too soon / Full / the "together" statuses of together-logic.ts.
//
// Pure: no runtime imports beyond availability-logic and together-logic, so
// e2e/day-tile-logic.spec.ts runs it under Node.

import { fixedStarts, formatLength, type Band, type DayStatus } from "./availability-logic";
import { togetherVerb } from "./together-logic";

type Noun = { one: string; other: string };

/**
 * Start buttons a collapsed tile shows: two rows of two (owner, 25 Sept:
 * "add the time chip 2 on a row"; "When more than 4 exist … add a 'show more
 * options'"). More than that and the tile offers "Show more options", which
 * opens THAT tile in place — never a scrollbar in a tile.
 */
export const TILE_MAX_STARTS = 4;

/**
 * A day of fixed starts whose every start the clock has passed has nothing
 * left to offer. Today that is "Too soon" (It's too late to book today); any
 * other day — which cannot happen with a sane clock — "Full". Two ways the
 * panel learns it:
 *
 * - the day's bands still hold the passed starts (read before the clock
 *   overtook them; a single start on the grid always counts as pickable, so
 *   the day would read "free");
 * - a fresh read of today returns NO band (the engine drops a start that has
 *   begun), so the hours-based check calls it "full" — with "Try fewer
 *   riders", which would not help. `todayStartsMs` are the fixed starts the
 *   page's OTHER days show, put on today's clock (fixedStartMinutes +
 *   the tenant's wall clock): when every one of them is behind the clock (and
 *   its notice), the day is over, not full.
 *
 * Every other status, and every day with a range or with a start still ahead,
 * is unchanged.
 */
export function fixedDayStatus(i: {
  status: DayStatus;
  bands: readonly Band[] | undefined;
  nowMs: number;
  isToday: boolean;
  /** The page's fixed start times, on today's clock ([] or absent: unknown). */
  todayStartsMs?: readonly number[] | null;
  noticeMin?: number;
}): DayStatus {
  if (i.status === "full" && i.isToday && (!i.bands || i.bands.length === 0)) {
    const starts = i.todayStartsMs ?? [];
    const floor = i.nowMs + Math.max(0, i.noticeMin ?? 0) * 60_000;
    return starts.length > 0 && starts.every((ms) => ms < floor) ? "notice" : i.status;
  }
  if (i.status !== "free" || !i.bands) return i.status;
  const fixed = fixedStarts(i.bands, i.nowMs);
  if (fixed == null || fixed.length > 0) return i.status;
  return i.isToday ? "notice" : "full";
}

/**
 * The tenant-local start times (minutes after midnight) of every day of
 * fixed starts in `lists`, ascending. Empty when any list is a range (the
 * option is not one of fixed starts, so nothing is inferred) or none has a band.
 */
export function fixedStartMinutes(
  lists: readonly (readonly Band[] | undefined)[],
  offsetAt: (ms: number) => number,
): number[] {
  const out = new Set<number>();
  for (const b of lists) {
    if (!b || b.length === 0) continue;
    const fixed = fixedStarts(b);
    if (fixed == null) return [];
    for (const ms of fixed) out.add((((Math.round(ms / 60_000) + offsetAt(ms)) % 1440) + 1440) % 1440);
  }
  return [...out].sort((a, b) => a - b);
}

/**
 * The words for a chosen start that fresh bands no longer contain: one the
 * clock has passed "has passed"; one someone took "is no longer free".
 */
export function droppedStartText(hm: string, startMs: number, nowMs: number): string {
  return startMs <= nowMs ? `${hm} has passed — pick another time or day.` : `${hm} is no longer free — pick another time.`;
}

/** The starts a tile offers itself: a free day of fixed starts; else null (the tile keeps its status). */
export function tileStarts(i: { status: DayStatus; bands: readonly Band[] | undefined; nowMs: number }): number[] | null {
  if (i.status !== "free" || !i.bands) return null;
  const fixed = fixedStarts(i.bands, i.nowMs);
  return fixed && fixed.length > 0 ? fixed : null;
}

/**
 * The tile row's layout, from every band list the page has read — the
 * choice's own, per day, and the single-option reads of together-logic.
 * "starts" once any of them is a day of fixed starts: the options run at fixed
 * times, so the tiles list them (and a day with none, such as a "Ride
 * together" day whose combination has no band at all, shows no time strip
 * either). "status" once any is a range (today's day radios, time strip and
 * start chips). With neither — the page is being read, or no day has a band —
 * the previous layout stays, so the row never jumps on a re-read.
 */
export type TileMode = "starts" | "status";
export function tileMode(evidence: readonly (readonly Band[] | undefined)[], previous: TileMode): TileMode {
  const lists = evidence.filter((b): b is readonly Band[] => !!b && b.length > 0);
  if (lists.some((b) => fixedStarts(b) != null)) return "starts";
  if (lists.length > 0) return "status";
  return previous;
}

/**
 * The tile's length line, once per tile. One length: "3 h 30 min". Options of
 * different lengths chosen together: the LONGEST, as "up to 5 h" — the tile
 * has room for one line, and the visit summary names each option's own
 * length. null when no length is known.
 */
export function tileLengthText(minutes: readonly number[]): string | null {
  const ms = [...new Set(minutes.filter((m) => Number.isFinite(m) && m > 0))];
  if (ms.length === 0) return null;
  const longest = Math.max(...ms);
  return ms.length === 1 ? formatLength(longest) : `up to ${formatLength(longest)}`;
}

const STATUS_TEXT: Record<DayStatus, string> = {
  idle: "",
  checking: "Checking…",
  unknown: "Not checked",
  free: "Free",
  full: "Full",
  closed: "Closed",
  not_offered: "Not offered",
  notice: "Too soon",
  together: "Go together",
  apart: "One at a time",
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * A day's status word. "together" takes the service's own verb: riders
 * "Ride together", anyone else "Go together" (together-logic togetherVerb).
 */
export function dayStatusText(status: DayStatus, noun: Noun): string {
  if (status === "together") return `${cap(togetherVerb(noun))} together`;
  return STATUS_TEXT[status];
}

/** What a screen reader hears for the status, where the short word needs its reason. */
export function dayStatusSpoken(status: DayStatus, noun: Noun): string {
  if (status === "not_offered") return "selected option not offered on this day";
  if (status === "together") return `not at the same time, ${togetherVerb(noun)} together on one option`;
  if (status === "apart") return "not at the same time, one option at a time";
  return dayStatusText(status, noun).toLowerCase();
}

/**
 * Which of a day's starts its tile shows, and its "Show more options" /
 * "Show fewer" button (owner, 25 Sept):
 *
 * - `max` (4) or fewer starts: all of them, no button.
 * - More, collapsed: the first `max`, and "Show more options" for the rest
 *   (`hidden` of them).
 * - More, opened by the guest: all of them, and "Show fewer".
 * - More, and the chosen start is beyond the first `max`: all of them and NO
 *   button — the tile stays open, so the guest's choice is never hidden
 *   (`forcedOpen`), whether or not they opened it.
 */
export type TileStartsView = {
  shown: number[];
  /** How many starts the collapsed tile hides (0 when open or when none). */
  hidden: number;
  /** The tile's button: "more" (Show more options), "fewer" (Show fewer), or none. */
  toggle: "more" | "fewer" | null;
  /** Every start is on show. */
  open: boolean;
  /** Open because the chosen start is beyond the first `max`. */
  forcedOpen: boolean;
};
export function tileStartsView(i: {
  starts: readonly number[];
  expanded: boolean;
  /** The chosen start, when this tile is the chosen day. */
  selectedStart: number | null;
  max?: number;
}): TileStartsView {
  const max = Math.max(1, Math.floor(i.max ?? TILE_MAX_STARTS));
  const starts = [...i.starts];
  if (starts.length <= max) return { shown: starts, hidden: 0, toggle: null, open: true, forcedOpen: false };
  const at = i.selectedStart == null ? -1 : starts.indexOf(i.selectedStart);
  const forcedOpen = at >= max;
  if (forcedOpen) return { shown: starts, hidden: 0, toggle: null, open: true, forcedOpen: true };
  if (i.expanded) return { shown: starts, hidden: 0, toggle: "fewer", open: true, forcedOpen: false };
  return { shown: starts.slice(0, max), hidden: starts.length - max, toggle: "more", open: false, forcedOpen: false };
}

export type DayTile =
  | { kind: "status"; status: DayStatus; text: string; spoken: string }
  | {
      kind: "starts";
      /** Every start the day offers, in order. */
      starts: number[];
      /** The ones on show (tileStartsView). */
      shown: number[];
      /** How many a collapsed tile hides ("Show more options"); 0: none hidden. */
      more: number;
      /** The tile's button: "more", "fewer", or none (tileStartsView). */
      toggle: "more" | "fewer" | null;
      /** More than `max` starts, all on show (opened, or held open by the choice). */
      expanded: boolean;
      lengthText: string | null;
    };

/**
 * One tile's content. A free day of fixed starts lists its starts: up to
 * `max` (4, two rows of two), then "Show more options", which opens the tile
 * to all of them in place (tileStartsView). The chosen start is never the one
 * hidden: a choice beyond the first `max` keeps the tile open. Every other
 * day is its status word.
 */
export function dayTile(i: {
  status: DayStatus;
  bands: readonly Band[] | undefined;
  nowMs: number;
  /** Each chosen option's length, in minutes. */
  minutes: readonly number[];
  /** The chosen start, when this tile is the chosen day. */
  selectedStart: number | null;
  expanded: boolean;
  noun: Noun;
  max?: number;
}): DayTile {
  const starts = tileStarts(i);
  if (!starts) return { kind: "status", status: i.status, text: dayStatusText(i.status, i.noun), spoken: dayStatusSpoken(i.status, i.noun) };
  const v = tileStartsView({ starts, expanded: i.expanded, selectedStart: i.selectedStart, max: i.max });
  return {
    kind: "starts",
    starts,
    shown: v.shown,
    more: v.hidden,
    toggle: v.toggle,
    expanded: v.toggle === "fewer" || v.forcedOpen,
    lengthText: tileLengthText(i.minutes),
  };
}
